import React, {FC, useEffect, useRef} from 'react';
import styled from 'styled-components';

import {usePrevious} from '../../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {StartGuideTaskTypesEnum} from '../../../../models/api/preferencesModel';
import {getIsInTrialOrManagedTrial} from '../../../../store/data/company/companySelectors';
import {updateStartGuideTasksAction} from '../../../../store/data/preferences/preferencesActions';
import {getWasOnboardingCompleted} from '../../../../store/data/preferences/preferencesSelectors';
import {useLegacyTaskCompletionProgress} from '../../../onboarding/admin/adminOnboardingHelpers';
import {useAdminOnboardingTasks} from '../../../onboarding/admin/tasks/adminOnboardingTasks';
import {computeOnboardingProgress} from '../../../onboarding/admin/tasks/onboardingTasks';
import {GuideButton} from './guideButton';
import {useOnboardingModalState} from './onboardingContext';
import {useSuspendedUntil} from './onboardingHelpers';
import {OnboardingWidget} from './onboardingWidget';

/*
 * Props.
 */

interface OnboardingProps {
  panelId: AppPanelId;
}

/*
 * Style.
 */

const StyledOnboardingButton = styled(ChromelessButton)`
  // Disable drag in Electron.
  -webkit-app-region: no-drag;
`;

/*
 * Component.
 */

export const Onboarding: FC<OnboardingProps> = ({panelId}) => {
  const {shouldCompleteSandbox, shouldAddCircle} = useLegacyTaskCompletionProgress();

  const wasOnboardingCompleted = useAppSelector(getWasOnboardingCompleted);
  const isInTrial = useAppSelector(getIsInTrialOrManagedTrial);

  const previousShouldCompleteSandbox = usePrevious(shouldCompleteSandbox);
  const previousShouldAddCircle = usePrevious(shouldAddCircle);

  const {
    isOnboardingChecklistModalOpen,
    setIsOnboardingChecklistModalOpen,
    isAdminWizardOpen,
    maybeOpenOnboardingChecklist,
  } = useOnboardingModalState();

  const buttonRef = useRef<HTMLDivElement>(null);

  const dispatch = useAppDispatch();

  // The inbox, channel, and rule creators handle this explicitly.
  useEffect(() => {
    if (!wasOnboardingCompleted) {
      return;
    }

    if (previousShouldCompleteSandbox && !shouldCompleteSandbox) {
      trackInterfaceEvent(InterfaceEventsEnum.P0_GETTING_STARTED_SUCCESS, {
        item: 'try_sandbox',
      });
    }

    if (previousShouldAddCircle && !shouldAddCircle) {
      if (!isAdminWizardOpen) {
        maybeOpenOnboardingChecklist();
      }

      trackInterfaceEvent(InterfaceEventsEnum.P0_GETTING_STARTED_SUCCESS, {
        item: 'create_teammate_group',
      });
    }
  });

  const taskGroups = useAdminOnboardingTasks();
  const {numCompletedTasks, numTotalTasks} = computeOnboardingProgress(taskGroups);

  const shouldUpdateHelpCenterSuccess = () => {
    if (shouldCompleteSandbox && !isInTrial) {
      // Do not show setup guide again if sandbox is the only item not completed.
      return numCompletedTasks >= numTotalTasks - 1;
    }

    return numCompletedTasks === numTotalTasks;
  };

  // Hold on to the task state from the last time we opened the setup guide, so we can animate it when re-opening it.
  const previousTaskGroups = useSuspendedUntil(taskGroups, isOnboardingChecklistModalOpen, 500);

  return (
    <>
      <StyledOnboardingButton
        ref={buttonRef}
        onClick={() => {
          if (isOnboardingChecklistModalOpen) {
            setIsOnboardingChecklistModalOpen(false);
            trackInterfaceEvent(InterfaceEventsEnum.P0_GETTING_STARTED_CLOSE, {
              type: 'user-action',
            });
          } else {
            setIsOnboardingChecklistModalOpen(true);
            trackInterfaceEvent(InterfaceEventsEnum.P0_GETTING_STARTED_OPEN, {
              type: 'user-action',
            });
          }
        }}
      >
        <GuideButton
          isSelected={isOnboardingChecklistModalOpen}
          numCompletedItems={numCompletedTasks}
          numTotalItems={numTotalTasks}
        />
      </StyledOnboardingButton>
      {isOnboardingChecklistModalOpen && (
        <OnboardingWidget
          panelId={panelId}
          previousTaskGroups={previousTaskGroups}
          taskGroups={taskGroups}
          onRequestClose={() => {
            setIsOnboardingChecklistModalOpen(false);
            trackInterfaceEvent(InterfaceEventsEnum.P0_GETTING_STARTED_CLOSE, {
              type: 'user-action',
            });
            if (shouldUpdateHelpCenterSuccess()) {
              dispatch(updateStartGuideTasksAction(StartGuideTaskTypesEnum.HELP_CENTER_SUCCESS_DISMISS));
            }
          }}
          anchor={buttonRef}
        />
      )}
    </>
  );
};
